import { Image } from 'expo-image';
import { useState } from 'react';
import React from 'react';
import { Platform, StyleSheet, TouchableOpacity, useWindowDimensions, View } from 'react-native';

import { npoEditedNotClaimed, npoThumbPlaceholder } from '@/assets/images';
import Text from '@/components/Text';
import { BLUR_HASH } from '@/constants/layout';
import { firebaseImage, ThumbSize } from '@/utils/firebaseImage';

import FavoriteButton from '../buttons/FavoriteButton';
import LightRatingButton from '../buttons/LightRatingButton';

interface NpoImageProps {
  source: string;
}

const NpoImage = ({ source }: NpoImageProps) => (
  <Image
    source={source}
    style={commonStyles.carouselImage}
    contentFit="cover"
    placeholder={{ blurhash: BLUR_HASH }}
    placeholderContentFit="cover"
  />
);

interface NonProfitOrganizationCardProps {
  npoId: string;
  title: string;
  subtitle?: string;
  assets: string[];
  lightRatingValue: number;
  onPress: () => void;
  onLightRatingPress: () => void;
  size?: number;
  showLightRating?: boolean;
  lightRatingVariant?: 'small' | 'medium' | 'large' | 'xlarge';
  lightRatingPositon?: number;
  showFavoriteButton?: boolean;
  claimed: boolean;
}

const NonProfitOrganizationCard = ({
  npoId,
  title,
  subtitle,
  assets,
  lightRatingValue,
  onPress,
  onLightRatingPress,
  size,
  showLightRating = true,
  lightRatingVariant = 'medium',
  lightRatingPositon = 14,
  showFavoriteButton = true,
  claimed
}: NonProfitOrganizationCardProps) => {
  const { width } = useWindowDimensions();

  const { style } = useStyle(lightRatingPositon, size, width);

  // Carousel commented so if we want to add it again, but for now removed to improve performance

  // const showCarousel = assets.length > 1;

  // const [activeSlide, setActiveSlide] = useState(showCarousel ? 0 : -1);
  const [startX, setStartX] = useState(0);
  // const [carouselWidth, setCarouselWidth] = useState((size ?? width - 36) !== 0 ? (size ?? width - 36) : 100);

  // useEffect(() => {
  //   if ((size ?? width - 36) !== 0) setCarouselWidth(size ?? width - 36);
  // }, [size, width]);

  return (
    <TouchableOpacity
      onPress={(e) => {
        if (Math.abs(e.nativeEvent.pageX - startX) < 5) {
          onPress();
        }
      }}
      onPressIn={(e) => setStartX(e.nativeEvent.pageX)}>
      <View style={[style.mainPressable, { width: size }]}>
        <View style={style.mainContainer}>
          {Platform.OS === 'web' ? (
            <div className="flex-1 flex" onDragStart={(e) => e.preventDefault()}>
              <NpoImage
                source={
                  claimed
                    ? assets.length > 0
                      ? firebaseImage(assets?.at(0), ThumbSize.Small)
                      : npoThumbPlaceholder
                    : npoEditedNotClaimed
                }
              />
            </div>
          ) : (
            <NpoImage
              source={
                claimed
                  ? assets.length > 0
                    ? firebaseImage(assets?.at(0), ThumbSize.Mid)
                    : npoThumbPlaceholder
                  : npoEditedNotClaimed
              }
            />
          )}
          {/* Carousel Assets */}
          {/* {showCarousel ? (
            <Carousel
              loop
              width={carouselWidth}
              {...(Platform.OS === 'web' && {
                height: carouselWidth
              })}
              pagingEnabled
              autoPlay
              autoPlayInterval={2000}
              onConfigurePanGesture={(panGesture) => {
                'worklet';
                panGesture.activeOffsetX([-10, 10]);
              }}
              data={[...assets]}
              scrollAnimationDuration={1000}
              onProgressChange={(_, absoluteProgress) => {
                // eslint-disable-next-line no-unused-expressions
                absoluteProgress > assets.length - 1 ? setActiveSlide(0) : setActiveSlide(Math.round(absoluteProgress));
              }}
              renderItem={({ index }) => {
                if (Platform.OS === 'web')
                  return (
                    <div className="flex-1 flex" onDragStart={(e) => e.preventDefault()}>
                      <NpoImage source={firebaseImage(assets?.at(index))} />
                    </div>
                  );
                return <NpoImage source={firebaseImage(assets?.at(index))} />;
              }}
            />
          ) : Platform.OS === 'web' ? (
            <div className="flex-1 flex" onDragStart={(e) => e.preventDefault()}>
              <NpoImage
                source={
                  claimed
                    ? assets.length > 0
                      ? firebaseImage(assets?.at(0))
                      : npoThumbPlaceholder
                    : npoEditedNotClaimed
                }
              />
            </div>
          ) : (
            <NpoImage
              source={
                claimed ? (assets.length > 0 ? firebaseImage(assets?.at(0)) : npoThumbPlaceholder) : npoEditedNotClaimed
              }
            />
          )} */}

          {/* {showCarousel && (
            <View style={style.dotsIndicator}>
              <Dots length={assets.length} active={activeSlide} activeColor="white" passiveColor="#C0C0C0" />
            </View>
          )} */}

          {/* Favorite Icon */}
          {showFavoriteButton && (
            <View style={style.favoriteIcon}>
              <FavoriteButton type="npo" itemId={npoId} />
            </View>
          )}

          {/* LightRating */}
          {showLightRating && (
            <View style={style.lightRatingContainer}>
              <LightRatingButton
                variant={lightRatingVariant}
                lightRatingValue={lightRatingValue}
                onPress={onLightRatingPress}
              />
            </View>
          )}
        </View>

        {/* Title */}
        <Text font="primary" fontWeight="900" style={style.title}>
          {title}
        </Text>
        {/* Subtitle */}
        {subtitle && (
          <Text fontWeight="450" style={style.subtitle} numberOfLines={1}>
            {subtitle}
          </Text>
        )}
      </View>
    </TouchableOpacity>
  );
};

export default React.memo(NonProfitOrganizationCard);

const commonStyles = StyleSheet.create({
  carouselImage: {
    flex: 1
  }
});

const useStyle = (lightRatingPositon: number, size?: number, width?: number) => {
  const style = StyleSheet.create({
    mainPressable: {
      flexDirection: 'column',
      alignItems: 'flex-start'
    },

    mainContainer: {
      width: size ?? (width ? width - 36 : 100),
      height: size ?? width ?? 0,
      borderRadius: 10,
      overflow: 'hidden'
    },

    dotsIndicator: {
      position: 'absolute',
      bottom: 10,
      left: 0,
      right: 0
    },

    favoriteIcon: {
      height: 28,
      width: 28,
      position: 'absolute',
      top: 14,
      right: 14,
      zIndex: 10
    },

    lightRatingContainer: {
      position: 'absolute',
      bottom: lightRatingPositon,
      right: lightRatingPositon,
      zIndex: 10,
      elevation: 10
    },

    title: {
      marginTop: 10,
      fontSize: 18,
      color: 'black'
    },

    subtitle: {
      marginTop: 5,
      fontSize: 14
    }
  });

  return { style, width };
};
